import * as m from '../../common/messages.js';
import {outgoing} from '../../common/messages.js';
import {agmDomainUri} from './constants.js';
import {AddressType, PeerId, Source} from '../../state/types/common.js';
import {
    AddInterest,
    Event,
    Invoke,
    MethodsAdded,
    MethodsRemoved,
    Register,
    RemoveInterest,
    Result,
    Subscribed,
    SubscriptionCancelled
} from './types/messages.js';
import {Reason} from '../../reason.js';
import {SendMessageAction} from '../../node.js';

function methodsAddedMsg(toPeerId: PeerId, serverId: PeerId, methods, sourceType: AddressType): MethodsAdded {
    return {
        domain: agmDomainUri,
        type: 'methods-added',
        peer_id: toPeerId,
        source_type: sourceType,
        server_id: serverId,
        methods
    }
}

export function methodsAdded(recipient: Source, toPeerId: PeerId, serverId: PeerId, methods, sourceType: AddressType): SendMessageAction<'methods-added'> {
    return m.outgoing(recipient, methodsAddedMsg(toPeerId, serverId, methods, sourceType));
}

export function register(requestId, serverId: PeerId, methods): Register {
    return {
        domain: agmDomainUri,
        type: 'register',
        request_id: requestId,
        peer_id: serverId,
        methods: methods
    }
}

function methodsRemovedMsg(peerId: PeerId, serverId: PeerId, methodIds): MethodsRemoved {
    return {
        domain: agmDomainUri,
        type: 'methods-removed',
        peer_id: peerId,
        server_id: serverId,
        methods: methodIds
    }
}

export function methodsRemoved(recipient: Source, fromPeerId: PeerId, serverId: PeerId, methodIds): SendMessageAction<'methods-removed'> {
    return m.outgoing(recipient, methodsRemovedMsg(fromPeerId, serverId, methodIds));
}

function invokeMsg(invocationId, peerId: PeerId, methodId, callerId: PeerId, args, argsKv, ctx): Invoke {
    return {
        domain: agmDomainUri,
        type: 'invoke',
        invocation_id: invocationId,
        peer_id: peerId,
        method_id: methodId,
        caller_id: callerId,
        arguments: args,
        arguments_kv: argsKv,
        context: ctx
    }
}

export function invoke(recipient: Source, invocationId, peerId: PeerId, methodId, callerId: PeerId, args, argsKv, ctx): SendMessageAction<'invoke'> {
    return outgoing(recipient, invokeMsg(invocationId, peerId, methodId, callerId, args, argsKv, ctx))
}

function resultMsg(requestId, callerId: PeerId, results): Result {
    return {
        domain: agmDomainUri,
        type: 'result',
        request_id: requestId,
        peer_id: callerId,
        result: results
    }
}

export function result(recipient: Source, requestId, callerId: PeerId, results) {
    return outgoing(recipient, resultMsg(requestId, callerId, results));
}

function addInterestMsg(subscriptionId, serverId: PeerId, methodId, subscriberId: PeerId, args, argsKv, flags, ctx): AddInterest {
    return {
        domain: agmDomainUri,
        type: 'add-interest',
        subscription_id: subscriptionId,
        peer_id: serverId,
        method_id: methodId,
        caller_id: subscriberId,
        arguments: args,
        arguments_kv: argsKv,
        flags: flags,
        context: ctx
    };
}

export function addInterest(recipient: Source, subscriptionId, serverId: PeerId, methodId, subscriberId: PeerId, args, argsKv, flags, ctx): SendMessageAction<'add-interest'> {
    return m.outgoing(recipient, addInterestMsg(subscriptionId, serverId, methodId, subscriberId, args, argsKv, flags, ctx));
}

function removeInterestMsg(subscriptionId, serverId: PeerId, methodId, subscriberId: PeerId, reason: Reason): RemoveInterest {
    return {
        domain: agmDomainUri,
        type: 'remove-interest',
        subscription_id: subscriptionId,
        peer_id: serverId,
        method_id: methodId,
        caller_id: subscriberId,
        reason_uri: reason.uri,
        reason: reason.message
    };
}

export function removeInterest(recipient: Source, subscriptionId, serverId: PeerId, methodId, subscriberId: PeerId, reason: Reason): SendMessageAction<'remove-interest'> {
    return m.outgoing(recipient, removeInterestMsg(subscriptionId, serverId, methodId, subscriberId, reason));
}

function subscribedMsg(requestId, subscriberId: PeerId, subscriptionId): Subscribed {
    return {
        domain: agmDomainUri,
        type: 'subscribed',
        request_id: requestId,
        peer_id: subscriberId,
        subscription_id: subscriptionId
    };
}

export function subscribed(recipient: Source, requestId, subscriberId: PeerId, subscriptionId): SendMessageAction<'subscribed'> {
    return m.outgoing(recipient, subscribedMsg(requestId, subscriberId, subscriptionId));
}

function eventMsg(subscriberId: PeerId, subscriptionId, isOob, sqn, isSnapshot, data): Event {
    return {
        domain: agmDomainUri,
        type: 'event',
        peer_id: subscriberId,
        subscription_id: subscriptionId,
        oob: isOob,
        sequence: sqn,
        snapshot: isSnapshot,
        data
    };
}

export function event(recipient: Source, subscriberId: PeerId, subscriptionId, isOob, sqn, isSnapshot, data): SendMessageAction<'event'> {
    return m.outgoing(recipient, eventMsg(subscriberId, subscriptionId, isOob, sqn, isSnapshot, data));
}

function subscriptionCancelledMsg(subscriptionId, subscriberId: PeerId, reason: Reason): SubscriptionCancelled {
    return {
        domain: agmDomainUri,
        type: 'subscription-cancelled',
        subscription_id: subscriptionId,
        peer_id: subscriberId,
        reason_uri: reason.uri,
        reason: reason.message
    };
}

export function subscriptionCancelled(recipient: Source, subscriptionId, subscriberId: PeerId, reason: Reason): SendMessageAction<'subscription-cancelled'> {
    return m.outgoing(recipient, subscriptionCancelledMsg(subscriptionId, subscriberId, reason));
}

export function peerRemoved(recipient: Source, peerId: PeerId, removedPeerId: PeerId, reason: Reason) {
    return m.peerRemoved(agmDomainUri, recipient, peerId, removedPeerId, reason);
}
